import { useState, useEffect, useCallback, useRef, useMemo, createContext, useContext, useReducer } from 'react'
import { useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { useMutation } from 'convex/react'
import { useAuthActions } from '@convex-dev/auth/react'
import { api } from '../../convex/_generated/api'
import { Id } from '../../convex/_generated/dataModel'
import { useAuth } from '@/contexts/auth-context'
import {
  isElectron, getDesktopSettings, setDesktopSettings,
  batchThumbnails, localMediaUrl, openFullDiskAccess,
  type NuxCluster, type DesktopSettings, type BlockedLibrary,
} from '@/lib/electron'
import { runNuxScan, extractClusterThumbnails, type ScanResult, type ScanProgress } from '@/lib/media-scan'
import { track } from '@/lib/track'
import { useWorkflowChat, type ChatMessage } from '@/hooks/useWorkflowChat'
import { useLocalToolBridge } from '@/hooks/useLocalToolBridge'
import { VisualToolWidget } from '@/components/workflow/VisualToolWidgets'
import { Loader2, Settings, Send, ArrowRight, Film, Check, X, ChevronLeft, ChevronRight, Play, Image, Music, List, LayoutGrid, Grid3x3, HardDrive } from 'lucide-react'
import { cn } from '@/lib/utils'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { AnimatePresence, motion } from 'motion/react'
import { PlyrPlayer } from '@/components/video/PlyrPlayer'
import { SITE_HOST } from '@/lib/config/site'

const DEVICE_ID_KEY = 'footage_device_id'

// --- Timeline Context ---

type ZoomLevel = 0 | 1 | 2
type ViewMode = 'list' | 'grid' | 'clusters'

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
}

type TimelineState = {
  clusters: NuxCluster[]
  selectedIds: Set<string>
  scanProgress: ScanProgress | null
  scanThumbnails: Map<string, string>
  scanDone: boolean
  expandedClusterId: string | null
  fileThumbnails: Map<string, string>
  lightbox: { clusterId: string; fileIndex: number } | null
  blockedLibraries: BlockedLibrary[]
  groupLimits: Map<string, number>
  zoomLevel: ZoomLevel
  viewMode: ViewMode
  focusedClusterIndex: number
}

type MergedCluster = {
  id: string
  label: string
  sourceIds: string[]
  file_count: number
  files: { path: string; name: string; type: string; size: number }[]
  media_types: string[]
  cover_thumbnail?: string
  thumbnails: string[]
  earliestDate?: string
  latestDate?: string
  folder_name: string
  folder_names: string[]
}

type TimelineGroupData = {
  key: string
  label: string
  dateLabel: string
  sortKey: number
  merged: MergedCluster[]
}

function mergeClusterGroup(clusters: (NuxCluster & { cover_thumbnail?: string })[]): MergedCluster {
  const allFiles = clusters.flatMap(c => c.files)
  const allTypes = Array.from(new Set(clusters.flatMap(c => c.media_types)))
  const thumbs = clusters.map(c => c.cover_thumbnail).filter(Boolean) as string[]
  const folders = Array.from(new Set(clusters.map(c => c.folder_name).filter(Boolean)))
  const earliest = clusters.reduce((min, c) => (!c.earliestDate ? min : (!min || c.earliestDate < min ? c.earliestDate : min)), '' as string)
  const latest = clusters.reduce((max, c) => (!c.latestDate ? max : (!max || c.latestDate > max ? c.latestDate : max)), '' as string)
  return {
    id: clusters.map(c => c.id).join('+'),
    label: clusters.length === 1 ? clusters[0].label : (folders.length === 1 ? folders[0] : `${clusters.length} groups`),
    sourceIds: clusters.map(c => c.id),
    file_count: clusters.reduce((s, c) => s + c.file_count, 0),
    files: allFiles,
    media_types: allTypes,
    cover_thumbnail: clusters[0].cover_thumbnail,
    thumbnails: thumbs,
    earliestDate: earliest || undefined,
    latestDate: latest || undefined,
    folder_name: folders[0] || '',
    folder_names: folders,
  }
}

function groupByZoom(
  clusters: (NuxCluster & { cover_thumbnail?: string })[],
  zoom: ZoomLevel,
): TimelineGroupData[] {
  if (clusters.length === 0) return []

  const buckets = new Map<string, { label: string; dateLabel: string; sortKey: number; clusters: (NuxCluster & { cover_thumbnail?: string })[] }>()

  for (const cluster of clusters) {
    let key = 'Undated'
    let label = 'Undated'
    let dateLabel = ''
    let sortKey = 0

    if (cluster.latestDate) {
      const d = new Date(cluster.latestDate)
      sortKey = d.getTime()
      const now = Date.now()
      const dayMs = 86400000
      const daysAgo = Math.floor((now - d.getTime()) / dayMs)

      if (zoom === 2) {
        // Individual clusters — unique key per cluster, grouped by date for timeline labels
        key = d.toISOString().slice(0, 10) + '/' + cluster.id
        if (daysAgo === 0) { label = 'Today'; dateLabel = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) }
        else if (daysAgo === 1) { label = 'Yesterday'; dateLabel = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) }
        else if (daysAgo < 7) { label = d.toLocaleDateString('en-US', { weekday: 'long' }); dateLabel = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) }
        else { label = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); dateLabel = `${d.getFullYear()}` }
      } else if (zoom === 1) {
        // Merge by day
        key = d.toISOString().slice(0, 10)
        if (daysAgo === 0) { label = 'Today'; dateLabel = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) }
        else if (daysAgo === 1) { label = 'Yesterday'; dateLabel = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) }
        else if (daysAgo < 7) { label = d.toLocaleDateString('en-US', { weekday: 'long' }); dateLabel = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) }
        else { label = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); dateLabel = `${d.getFullYear()}` }
      } else {
        // Merge by month
        key = `${d.getFullYear()}-${String(d.getMonth()).padStart(2, '0')}`
        label = d.toLocaleDateString('en-US', { month: 'long' })
        dateLabel = `${d.getFullYear()}`
      }
    }

    if (!buckets.has(key)) buckets.set(key, { label, dateLabel, sortKey, clusters: [] })
    buckets.get(key)!.clusters.push(cluster)
  }

  // For zoom 2 (individual), re-group by date for timeline labels
  if (zoom === 2) {
    const dateGroups = new Map<string, TimelineGroupData>()
    for (const [, bucket] of buckets) {
      const c = bucket.clusters[0]
      const dateKey = c.latestDate ? new Date(c.latestDate).toISOString().slice(0, 10) : 'Undated'
      if (!dateGroups.has(dateKey)) {
        dateGroups.set(dateKey, { key: dateKey, label: bucket.label, dateLabel: bucket.dateLabel, sortKey: bucket.sortKey, merged: [] })
      }
      dateGroups.get(dateKey)!.merged.push(mergeClusterGroup(bucket.clusters))
    }
    return Array.from(dateGroups.values()).sort((a, b) => b.sortKey - a.sortKey)
  }

  // For zoom 0 (month) and 1 (day): each bucket produces one merged cluster
  return Array.from(buckets.entries())
    .map(([key, bucket]) => ({
      key,
      label: bucket.label,
      dateLabel: bucket.dateLabel,
      sortKey: bucket.sortKey,
      merged: [mergeClusterGroup(bucket.clusters)],
    }))
    .sort((a, b) => b.sortKey - a.sortKey)
}

type TimelineAction =
  | { type: 'SET_CLUSTERS'; clusters: NuxCluster[] }
  | { type: 'TOGGLE_CLUSTER'; id: string }
  | { type: 'SELECT_ALL' }
  | { type: 'DESELECT_ALL' }
  | { type: 'SET_SCAN_PROGRESS'; progress: ScanProgress }
  | { type: 'SET_THUMBNAIL'; clusterId: string; dataUrl: string }
  | { type: 'SET_SCAN_DONE' }
  | { type: 'EXPAND_CLUSTER'; id: string | null }
  | { type: 'SET_FILE_THUMBNAILS'; thumbnails: Record<string, string | null> }
  | { type: 'OPEN_LIGHTBOX'; clusterId: string; fileIndex: number }
  | { type: 'CLOSE_LIGHTBOX' }
  | { type: 'LIGHTBOX_PREV' }
  | { type: 'LIGHTBOX_NEXT'; maxIndex: number }
  | { type: 'SET_BLOCKED_LIBRARIES'; libraries: BlockedLibrary[] }
  | { type: 'RESCAN_START' }
  | { type: 'SHOW_MORE'; group: string; count: number }
  | { type: 'SET_ZOOM'; level: ZoomLevel }
  | { type: 'SET_VIEW_MODE'; mode: ViewMode }
  | { type: 'FOCUS_CLUSTER'; index: number }

function timelineReducer(state: TimelineState, action: TimelineAction): TimelineState {
  switch (action.type) {
    case 'SET_CLUSTERS':
      return { ...state, clusters: action.clusters, selectedIds: new Set<string>() }
    case 'TOGGLE_CLUSTER': {
      const next = new Set(state.selectedIds)
      if (next.has(action.id)) next.delete(action.id)
      else next.add(action.id)
      return { ...state, selectedIds: next }
    }
    case 'SELECT_ALL':
      return { ...state, selectedIds: new Set(state.clusters.map(c => c.id)) }
    case 'DESELECT_ALL':
      return { ...state, selectedIds: new Set() }
    case 'SET_SCAN_PROGRESS':
      return { ...state, scanProgress: action.progress }
    case 'SET_THUMBNAIL': {
      const next = new Map(state.scanThumbnails)
      next.set(action.clusterId, action.dataUrl)
      return { ...state, scanThumbnails: next }
    }
    case 'SET_SCAN_DONE':
      return { ...state, scanDone: true }
    case 'EXPAND_CLUSTER':
      return { ...state, expandedClusterId: state.expandedClusterId === action.id ? null : action.id }
    case 'SET_FILE_THUMBNAILS': {
      const next = new Map(state.fileThumbnails)
      for (const [k, v] of Object.entries(action.thumbnails)) {
        if (v) next.set(k, v)
      }
      return { ...state, fileThumbnails: next }
    }
    case 'OPEN_LIGHTBOX':
      return { ...state, lightbox: { clusterId: action.clusterId, fileIndex: action.fileIndex } }
    case 'CLOSE_LIGHTBOX':
      return { ...state, lightbox: null }
    case 'LIGHTBOX_PREV':
      if (!state.lightbox || state.lightbox.fileIndex <= 0) return state
      return { ...state, lightbox: { ...state.lightbox, fileIndex: state.lightbox.fileIndex - 1 } }
    case 'LIGHTBOX_NEXT':
      if (!state.lightbox || state.lightbox.fileIndex >= action.maxIndex) return state
      return { ...state, lightbox: { ...state.lightbox, fileIndex: state.lightbox.fileIndex + 1 } }
    case 'SET_BLOCKED_LIBRARIES':
      return { ...state, blockedLibraries: action.libraries }
    case 'RESCAN_START':
      return { ...state, scanDone: false, clusters: [], selectedIds: new Set(), scanThumbnails: new Map(), fileThumbnails: new Map(), blockedLibraries: [], groupLimits: new Map(), zoomLevel: state.zoomLevel, viewMode: state.viewMode, focusedClusterIndex: -1 }
    case 'SHOW_MORE': {
      const next = new Map(state.groupLimits)
      next.set(action.group, action.count)
      return { ...state, groupLimits: next }
    }
    case 'SET_ZOOM':
      return { ...state, zoomLevel: action.level }
    case 'SET_VIEW_MODE':
      return { ...state, viewMode: action.mode }
    case 'FOCUS_CLUSTER':
      return { ...state, focusedClusterIndex: action.index }
    default:
      return state
  }
}

const TimelineContext = createContext<{
  state: TimelineState
  dispatch: React.Dispatch<TimelineAction>
  rescan: () => void
} | null>(null)

function useTimeline() {
  const ctx = useContext(TimelineContext)
  if (!ctx) throw new Error('useTimeline must be used within TimelineProvider')
  return ctx
}

export default function NuxPage() {
  const navigate = useNavigate()
  const { user, isLoading: isAuthLoading } = useAuth()
  const completeNux = useMutation(api.userProfiles.completeNux)
  const createWorkflow = useMutation(api.workflows.create)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const claimNuxWorkflow = useMutation((api.workflows as any).claimNuxWorkflow)
  const addDroppedFiles = useMutation(api.assets.addDroppedAssets)
  const addLocalAssets = useMutation(api.assets.addLocalAssets)
  const updateWorkflow = useMutation(api.workflows.update)
  const saveNuxState = useMutation(api.userProfiles.saveNuxState)
  const clearNuxState = useMutation(api.userProfiles.clearNuxState)
  const mergeAnonymousUser = useMutation(api.userProfiles.mergeAnonymousUser)
  const { signIn } = useAuthActions()
  const [showAuthModal, setShowAuthModal] = useState(false)

  const [timelineState, timelineDispatch] = useReducer(timelineReducer, {
    clusters: [],
    selectedIds: new Set<string>(),
    scanProgress: null,
    scanThumbnails: new Map(),
    scanDone: false,
    expandedClusterId: null,
    fileThumbnails: new Map(),
    lightbox: null,
    blockedLibraries: [],
    groupLimits: new Map(),
    zoomLevel: 1 as ZoomLevel,
    viewMode: 'grid' as ViewMode,
    focusedClusterIndex: -1,
  })

  const [screenIndex, setScreenIndex] = useState(0)
  const [workflowId, setWorkflowId] = useState<Id<'workflows'> | null>(null)
  const [shortcode, setShortcode] = useState<string | null>(null)
  const [scanResult, setScanResult] = useState<ScanResult | null>(null)
  const [scanSent, setScanSent] = useState(false)
  const [showTextInput, setShowTextInput] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [initError, setInitError] = useState<string | null>(null)
  const completingRef = useRef(false)
  const initRef = useRef(false)
  const prevAssistantCountRef = useRef(0)
  const claimRef = useRef(false)

  const {
    messages,
    isStreaming,
    sendMessage,
    hasLoaded,
    error: chatError,
  } = useWorkflowChat({
    workflowId,
    onToolComplete: useCallback((toolName: string, result: unknown) => {
      if (toolName === 'complete_nux' && !completingRef.current) {
        completingRef.current = true
        const data = result as { data?: { type?: string; title?: string } }
        if (data?.data?.type === 'nux_complete') {
          completeNux({ hadDirection: true }).then(() => {
            if (shortcode) {
              navigate(`/video/${shortcode}`, { replace: true })
            } else {
              navigate('/videos', { replace: true })
            }
          })
        }
      }
    }, [completeNux, navigate, shortcode]),
  })

  useLocalToolBridge(workflowId)

  const assistantMessages = useMemo(
    () => messages.filter(m => m.role === 'assistant'),
    [messages],
  )

  useEffect(() => {
    const finishedCount = assistantMessages.filter(m => !m.isStreaming).length
    if (finishedCount > prevAssistantCountRef.current) {
      prevAssistantCountRef.current = finishedCount
      setScreenIndex(finishedCount)
      setShowTextInput(false)
      setPendingChoice(false)
    }
  }, [assistantMessages])

  useEffect(() => {
    if (isAuthLoading) return
    if (!isElectron()) {
      navigate('/', { replace: true })
      return
    }
    if (user?.nuxCompleted) {
      navigate('/videos', { replace: true })
    }
  }, [isAuthLoading, user, navigate])

  // Post-OAuth: recover NUX workflow when user upgrades from anonymous.
  // First try merging by deviceId (handles anon→existing user case),
  // then fall back to claimNuxWorkflow for the single-workflow transfer.
  useEffect(() => {
    if (isAuthLoading || !user || user.isAnonymous || claimRef.current) return
    const savedWorkflowId = user.nuxWorkflowId as Id<'workflows'> | undefined
    if (!savedWorkflowId) return
    claimRef.current = true

    const recover = async () => {
      // Try deviceId-based merge first (transfers all data from orphaned anon profile)
      const deviceId = localStorage.getItem(DEVICE_ID_KEY)
      if (deviceId) {
        await mergeAnonymousUser({ deviceId }).catch(() => {})
      }

      // Claim the specific workflow if it wasn't already transferred by merge
      await claimNuxWorkflow({ workflowId: savedWorkflowId }).catch(() => {})

      await clearNuxState()
      setWorkflowId(savedWorkflowId)
      setScanSent(true)
      timelineDispatch({ type: 'SET_SCAN_DONE' })
    }

    recover().catch((err) => {
      console.warn('Failed to recover NUX workflow:', err)
      clearNuxState().catch(() => {})
    })
  }, [isAuthLoading, user, claimNuxWorkflow, mergeAnonymousUser, clearNuxState])

  useEffect(() => {
    if (isAuthLoading || !user || initRef.current) return
    // Skip normal init if we're recovering a workflow from OAuth
    if (user.nuxWorkflowId && !user.isAnonymous) return
    initRef.current = true

    const init = async () => {
      track('nux.started', {})

      const result = await createWorkflow({ isNux: true, title: 'Welcome' })
      setWorkflowId(result.id)
      setShortcode(result.shortcode)

      const scanStart = Date.now()
      let scan: ScanResult | null = null
      try {
        scan = await runNuxScan((p) => {
          timelineDispatch({ type: 'SET_SCAN_PROGRESS', progress: p })
        })
      } catch (e) {
        console.warn('NUX scan failed:', e)
      }

      const elapsed = Date.now() - scanStart
      if (elapsed < 3000) {
        await new Promise(r => setTimeout(r, 3000 - elapsed))
      }

      setScanResult(scan)
      if (scan?.clusters) {
        const fileCount = scan.clusters.reduce((s, c) => s + c.files.length, 0)
        const folderCount = new Set(scan.clusters.map(c => c.folder_path)).size
        track('nux.scan_completed', {
          fileCount,
          folderCount,
          clusterCount: scan.clusters.length,
        })
        timelineDispatch({ type: 'SET_CLUSTERS', clusters: scan.clusters })
      }
      if (scan?.blockedLibraries?.length) {
        timelineDispatch({ type: 'SET_BLOCKED_LIBRARIES', libraries: scan.blockedLibraries })
      }
      timelineDispatch({ type: 'SET_SCAN_DONE' })

      // Background work — thumbnails first, then media library sync
      // Sequential to avoid competing for the main thread
      if (scan?.clusters) {
        await extractClusterThumbnails(scan.clusters, (clusterId, dataUrl) => {
          timelineDispatch({ type: 'SET_THUMBNAIL', clusterId, dataUrl })
        })
        // Batch addDroppedFiles with generous yields between batches
        const BATCH_SIZE = 200
        const allFiles = scan.clusters.flatMap(c =>
          c.files.map(f => ({
            filePath: f.path,
            fileName: f.name,
            folder: c.folder_path,
            fileSize: f.size,
          }))
        )
        for (let i = 0; i < allFiles.length; i += BATCH_SIZE) {
          const batch = allFiles.slice(i, i + BATCH_SIZE)
          await new Promise(r => setTimeout(r, 50))
          await addDroppedFiles({ files: batch }).catch(console.warn)
        }
      }
    }

    init().catch((err) => {
      console.error('NUX init error:', err)
      setInitError(err instanceof Error ? err.message : 'Failed to initialize')
      timelineDispatch({ type: 'SET_SCAN_DONE' })
    })
  }, [isAuthLoading, user, createWorkflow])

  useEffect(() => {
    if (scanSent || !workflowId || !hasLoaded || !timelineState.scanDone) return
    setScanSent(true)

    if (scanResult && scanResult.clusters.length > 0) {
      const now = Date.now()
      const sorted = [...scanResult.clusters].sort((a, b) => {
        const aDate = a.latestDate ? new Date(a.latestDate).getTime() : 0
        const bDate = b.latestDate ? new Date(b.latestDate).getTime() : 0
        const aRecency = Math.max(0, 1 - (now - aDate) / (365 * 24 * 60 * 60 * 1000))
        const bRecency = Math.max(0, 1 - (now - bDate) / (365 * 24 * 60 * 60 * 1000))
        const aScore = a.file_count + aRecency * 100
        const bScore = b.file_count + bRecency * 100
        return bScore - aScore
      })
      const top = sorted.slice(0, 50)
      const clusterSummary = top
        .map(c => `- ${c.label} (${c.file_count} files, ${c.date_range}) — ${c.folder_path}`)
        .join('\n')
      const rest = sorted.length > 50 ? `\n...and ${sorted.length - 50} more smaller groups` : ''
      updateWorkflow({
        id: workflowId!,
        globalContext: `Scanned ${scanResult.files} media files in ${scanResult.clusters.length} groups (top ${top.length}):\n${clusterSummary}${rest}`,
      }).catch(() => {})
      sendMessage(`I just installed Footage and scanned my computer. Found ${scanResult.files} media files in ${scanResult.clusters.length} groups. What should we make first?`)
    } else {
      sendMessage("I just installed Footage. I don't have much media on this machine yet, but I'd like to get started.")
    }
  }, [scanResult, timelineState.scanDone, workflowId, hasLoaded, scanSent, sendMessage])

  // Retry if agent hasn't responded within 15s of sending
  const retryCountRef = useRef(0)
  useEffect(() => {
    if (!scanSent || assistantMessages.length > 0 || !workflowId || retryCountRef.current >= 2) return
    const timer = setTimeout(() => {
      retryCountRef.current++
      console.warn(`[nux] Agent not responding after 15s, retrying (attempt ${retryCountRef.current})`)
      sendMessage('Hello?')
    }, 15_000)
    return () => clearTimeout(timer)
  }, [scanSent, assistantMessages.length, workflowId, sendMessage])

  // Log chat errors
  useEffect(() => {
    if (chatError) console.error(`[nux] Chat error: ${chatError}`)
  }, [chatError])

  const requireAuth = useCallback(async (action: string, onAuthed: () => void) => {
    if (!user?.isAnonymous) { onAuthed(); return }
    // Save NUX state to profile before showing auth modal (must complete before OAuth redirect)
    if (workflowId) {
      await saveNuxState({ workflowId, pendingAction: action })
    }
    setShowAuthModal(true)
  }, [user, workflowId, saveNuxState])

  const handleSignInWithGoogle = useCallback(() => {
    signIn('google', { redirectTo: `${window.location.origin}/nux` })
  }, [signIn])

  const handleSendText = useCallback((text: string) => {
    if (!text.trim() || isStreaming) return
    setInputValue('')
    setShowTextInput(false)
    sendMessage(text.trim())
  }, [isStreaming, sendMessage])

  const handleSubmit = useCallback((e?: React.FormEvent) => {
    e?.preventDefault()
    requireAuth(inputValue, () => handleSendText(inputValue))
  }, [inputValue, handleSendText, requireAuth])

  const [pendingChoice, setPendingChoice] = useState(false)

  const handleChoiceClick = useCallback((action: string) => {
    if (isStreaming) return
    requireAuth(action, () => {
      setPendingChoice(true)
      sendMessage(action)
    })
  }, [isStreaming, sendMessage, requireAuth])

  const handleSendSelection = useCallback((message: string) => {
    requireAuth(message, () => {
      setPendingChoice(true)
      sendMessage(message)
      if (workflowId && timelineState.clusters.length > 0) {
        const selectedFiles = timelineState.clusters
          .filter(c => timelineState.selectedIds.has(c.id))
          .flatMap(c => c.files)
          .filter(f => f.type === 'image' || f.type === 'video')
        track('nux.clusters_selected', {
          clusterCount: timelineState.selectedIds.size,
          fileCount: selectedFiles.length,
        })
        if (selectedFiles.length > 0) {
          addLocalAssets({
            workflowId,
            files: selectedFiles.slice(0, 500).map(f => ({
              path: f.path,
              name: f.name,
              type: f.type as 'image' | 'video',
              size: f.size,
            })),
          }).catch(() => {})
        }
      }
      timelineDispatch({ type: 'DESELECT_ALL' })
    })
  }, [sendMessage, requireAuth, timelineDispatch, workflowId, timelineState.clusters, timelineState.selectedIds, addLocalAssets])

  const handleSkip = useCallback(async () => {
    await completeNux({ hadDirection: false })
    navigate('/videos', { replace: true })
  }, [completeNux, navigate])

  const injectThumbnails = useCallback((data: Record<string, unknown>): Record<string, unknown> => {
    if (!scanResult?.clusters?.length) return data
    const clusters = data.clusters as Array<Record<string, unknown>> | undefined
    if (!clusters) return data
    const scanMap = new Map(scanResult.clusters.map(c => [c.id, c]))
    const folderMap = new Map(scanResult.clusters.map(c => [c.folder_path, c]))
    const enriched = clusters.map(c => {
      const scanCluster = scanMap.get(c.id as string)
        || folderMap.get(c.folder_path as string)
        || scanResult.clusters.find(sc =>
          sc.label === c.label || sc.folder_path === c.folder_path
        )
      if (scanCluster?.cover_thumbnail && !c.cover_thumbnail) {
        return { ...c, cover_thumbnail: scanCluster.cover_thumbnail }
      }
      return c
    })
    return { ...data, clusters: enriched }
  }, [scanResult])

  const handleRescan = useCallback(async () => {
    timelineDispatch({ type: 'RESCAN_START' })
    try {
      const scan = await runNuxScan((p) => {
        timelineDispatch({ type: 'SET_SCAN_PROGRESS', progress: p })
      })
      setScanResult(scan)
      if (scan?.clusters) {
        timelineDispatch({ type: 'SET_CLUSTERS', clusters: scan.clusters })
        extractClusterThumbnails(scan.clusters, (clusterId, dataUrl) => {
          timelineDispatch({ type: 'SET_THUMBNAIL', clusterId, dataUrl })
        })
      }
      if (scan?.blockedLibraries?.length) timelineDispatch({ type: 'SET_BLOCKED_LIBRARIES', libraries: scan.blockedLibraries })
    } catch (e) {
      console.warn('Rescan failed:', e)
    }
    timelineDispatch({ type: 'SET_SCAN_DONE' })
  }, [])

  if (isAuthLoading) {
    return (
      <div className="min-h-screen bg-neutral-950 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-neutral-600" />
      </div>
    )
  }

  const totalSteps = 2
  const currentStep = screenIndex === 0 ? 0 : Math.min(screenIndex, totalSteps)

  const currentAssistant = screenIndex === 0
    ? (scanSent ? assistantMessages[0] ?? null : null)
    : (assistantMessages[screenIndex - 1] ?? null)
  const isWaitingForAgent = screenIndex > 0 && !currentAssistant
  const isCurrentStreaming = !!(currentAssistant?.isStreaming)
  const suggestedActions = (!isStreaming && currentAssistant && !isCurrentStreaming)
    ? (currentAssistant.suggestedActions || [])
    : []

  const showScanningPhase = screenIndex === 0 && !scanSent

  // Timeout: if loading for >30s with no agent response, skip loading phase
  const [loadingTimedOut, setLoadingTimedOut] = useState(false)
  const agentPending = scanSent && !currentAssistant && screenIndex === 0
  useEffect(() => {
    if (!agentPending) { setLoadingTimedOut(false); return }
    const timer = setTimeout(() => setLoadingTimedOut(true), 30_000)
    return () => clearTimeout(timer)
  }, [agentPending])

  const showLoadingPhase = !loadingTimedOut && (pendingChoice || isWaitingForAgent || (isCurrentStreaming && screenIndex > 0) || (screenIndex === 0 && scanSent && !currentAssistant))

  const hasSelection = timelineState.selectedIds.size > 0

  return (
    <TimelineContext.Provider value={{ state: timelineState, dispatch: timelineDispatch, rescan: handleRescan }}>
      <div className="h-screen bg-neutral-950 text-white flex flex-col overflow-hidden">
        <Helmet><title>Welcome - Footage</title></Helmet>

        <div className="flex items-center justify-between px-8 pt-[calc(var(--electron-titlebar-height,0px)+20px)] pb-3 z-20 shrink-0 electron-drag">
          <img src="/logo-sq-white.png" alt="Footage" className="h-7 w-7 rounded-md opacity-90" />
          {currentStep > 0 && (
            <div className="flex items-center gap-1.5">
              {Array.from({ length: totalSteps }).map((_, i) => (
                <div
                  key={i}
                  className={cn(
                    'h-1 rounded-full transition-all duration-500',
                    i < currentStep ? 'w-5 bg-white/30' : 'w-1.5 bg-white/10',
                  )}
                />
              ))}
            </div>
          )}
          <button onClick={handleSkip} className="text-xs text-neutral-600 hover:text-neutral-400 transition-colors">
            Skip
          </button>
        </div>

        <DevPanel user={user} workflowId={workflowId} />

        <div className="flex-1 flex min-h-0">
          <div className="w-[400px] shrink-0 flex flex-col border-r border-neutral-800/50 overflow-hidden">
            <AnimatePresence mode="wait">
              {hasSelection ? (
                <motion.div key="selection" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }} className="flex-1 flex flex-col">
                  <NuxSelectionPanel onSend={handleSendSelection} />
                </motion.div>
              ) : showScanningPhase ? (
                <motion.div key="scanning" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, y: -30 }} transition={{ duration: 0.5 }} className="flex-1 flex items-center justify-center px-6">
                  {initError ? (
                    <InitErrorView error={initError} onRetry={() => window.location.reload()} onSkip={handleSkip} />
                  ) : (
                    <NuxScanningCompact progress={timelineState.scanProgress} />
                  )}
                </motion.div>
              ) : showLoadingPhase ? (
                <motion.div key={`loading-${screenIndex}`} initial={{ opacity: 1 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }} className="flex-1 flex items-center justify-center">
                  <NuxLoadingScreen step={screenIndex} />
                </motion.div>
              ) : currentAssistant ? (
                <motion.div key={`screen-${screenIndex}`} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }} className="flex-1 flex flex-col justify-center overflow-y-auto">
                  <NuxAgentPanel
                    message={currentAssistant}
                    suggestedActions={suggestedActions}
                    showTextInput={showTextInput}
                    inputValue={inputValue}
                    onInputChange={setInputValue}
                    onSubmit={handleSubmit}
                    onChoiceClick={handleChoiceClick}
                    onShowTextInput={() => setShowTextInput(true)}
                    onSendSelection={handleSendSelection}
                    injectThumbnails={injectThumbnails}
                  />
                </motion.div>
              ) : loadingTimedOut ? (
                <motion.div key="timeout-fallback" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="flex-1 flex flex-col justify-center px-8 gap-6">
                  <p className="text-lg text-neutral-300">Select some clips from the timeline to get started.</p>
                  <div className="flex gap-3">
                    <button onClick={() => { setLoadingTimedOut(false); retryCountRef.current = 0; setScanSent(false) }} className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-sm font-medium transition-colors">
                      Retry
                    </button>
                    <button onClick={handleSkip} className="px-4 py-2 rounded-lg bg-neutral-800 hover:bg-neutral-700 text-sm font-medium transition-colors">
                      Skip to Studio
                    </button>
                  </div>
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>

          <div className="flex-1 overflow-y-auto min-w-0">
            <NuxMediaTimeline />
          </div>
        </div>

        <MediaLightbox />

        <AnimatePresence>
          {showAuthModal && (
            <NuxAuthModal
              onSignInWithGoogle={handleSignInWithGoogle}
              onClose={() => setShowAuthModal(false)}
            />
          )}
        </AnimatePresence>
      </div>
    </TimelineContext.Provider>
  )
}

// --- NuxScanningCompact ---

function NuxScanningCompact({ progress }: { progress: ScanProgress | null }) {
  const fileCount = progress?.filesFound ?? 0
  const currentDir = progress?.currentDir

  const shortFolder = useMemo(() => {
    if (!currentDir) return ''
    const parts = currentDir.replace(/\/$/, '').split('/')
    return parts.length > 2 ? `.../${parts.slice(-2).join('/')}` : currentDir
  }, [currentDir])

  const pct = progress && progress.totalDirs > 0
    ? Math.min(100, Math.round((progress.dirsScanned / progress.totalDirs) * 100))
    : 0

  return (
    <div className="flex flex-col items-center text-center w-full max-w-xs">
      {fileCount > 0 ? (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-3">
          <span className="text-4xl font-bold text-white tabular-nums tracking-tight">{fileCount.toLocaleString()}</span>
          <span className="text-base text-neutral-500 ml-2">files found</span>
        </motion.div>
      ) : (
        <div className="mb-3">
          <span className="text-lg text-neutral-400">Looking for your media...</span>
        </div>
      )}
      {shortFolder ? (
        <motion.div key={shortFolder} initial={{ opacity: 0 }} animate={{ opacity: 0.5 }} className="text-xs text-neutral-600 font-mono mb-6 h-4 truncate max-w-[280px]">
          {shortFolder}
        </motion.div>
      ) : (
        <div className="mb-6 h-4" />
      )}
      <div className="w-32 h-1 bg-neutral-800 rounded-full overflow-hidden">
        <div className="h-full bg-blue-500 rounded-full transition-all duration-300" style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}

// --- NuxLoadingScreen ---

const LOADING_PHASES = [
  ['Reviewing your media...', 'Organizing your memories...', 'Looking at what you captured...', 'Preparing your timeline...'],
  ['Setting up your film...', 'Getting the creative engine ready...', 'Thinking about your story...', 'Almost there...'],
]

function NuxLoadingScreen({ step }: { step: number }) {
  const phases = LOADING_PHASES[Math.min(step, LOADING_PHASES.length - 1)]
  const [phaseIdx, setPhaseIdx] = useState(0)
  const [visible, setVisible] = useState(true)

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false)
      setTimeout(() => {
        setPhaseIdx(i => (i + 1) % phases.length)
        setVisible(true)
      }, 300)
    }, 2800)
    return () => clearInterval(interval)
  }, [phases.length])

  return (
    <div className="flex flex-col items-center gap-4">
      <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
      <p className={cn('text-sm text-neutral-500 transition-opacity duration-300', visible ? 'opacity-100' : 'opacity-0')}>
        {phases[phaseIdx]}
      </p>
    </div>
  )
}

// --- NuxSelectionPanel ---

function NuxSelectionPanel({ onSend }: { onSend: (msg: string) => void }) {
  const { state, dispatch } = useTimeline()
  const { selectedIds, clusters, scanThumbnails } = state
  const [prompt, setPrompt] = useState('')

  const selected = useMemo(() => clusters.filter(c => selectedIds.has(c.id)), [clusters, selectedIds])
  const totalFiles = selected.reduce((s, c) => s + c.file_count, 0)

  const handleSend = () => {
    const text = prompt.trim() || `What should we make with these ${totalFiles} files from ${selected.length} group${selected.length > 1 ? 's' : ''}?`
    onSend(text)
    dispatch({ type: 'DESELECT_ALL' })
  }

  return (
    <div className="flex-1 flex flex-col px-8 py-6 gap-5">
      <div className="flex items-center justify-between">
        <span className="text-xs text-neutral-500 uppercase tracking-wider">Selected</span>
        <button onClick={() => dispatch({ type: 'DESELECT_ALL' })} className="text-[11px] text-neutral-600 hover:text-neutral-400 transition-colors">
          Clear
        </button>
      </div>

      <div className="flex flex-wrap gap-2.5">
        <AnimatePresence>
          {selected.map((c, i) => (
            <motion.div
              key={c.id}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.2, delay: i * 0.03 }}
              className="relative group"
            >
              <div className="w-14 h-14 rounded-xl overflow-hidden bg-neutral-800 ring-1 ring-white/10">
                {scanThumbnails.get(c.id) ? (
                  <img src={scanThumbnails.get(c.id)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Film size={16} className="text-neutral-600" />
                  </div>
                )}
              </div>
              <button
                onClick={() => dispatch({ type: 'TOGGLE_CLUSTER', id: c.id })}
                className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-neutral-700 border border-neutral-600 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-neutral-600"
              >
                <X size={9} />
              </button>
              <p className="mt-1 text-[9px] text-neutral-600 truncate max-w-[56px] text-center">{c.label}</p>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      <div className="mt-auto flex flex-col gap-3">
        <p className="text-[11px] text-neutral-600">
          {totalFiles.toLocaleString()} files · {selected.length} group{selected.length > 1 ? 's' : ''}
        </p>
        <textarea
          value={prompt}
          onChange={e => setPrompt(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend() } }}
          placeholder="What should we make with these?"
          rows={2}
          className="bg-neutral-900 border border-neutral-700 rounded-xl px-4 py-3 text-[14px] text-white placeholder:text-neutral-500 focus:outline-none focus:border-blue-500/50 transition-colors resize-none"
        />
        <button
          onClick={handleSend}
          className="w-full px-4 py-3 rounded-xl bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-500 transition-colors flex items-center justify-center gap-2"
        >
          <span>Create film</span>
          <ArrowRight size={14} />
        </button>
      </div>
    </div>
  )
}

// --- NuxAuthModal ---

function NuxAuthModal({ onSignInWithGoogle, onClose }: { onSignInWithGoogle: () => void; onClose: () => void }) {
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [step, setStep] = useState<'email' | 'code'>('email')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { signIn } = useAuthActions()

  const handleEmail = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.trim()) return
    setIsLoading(true)
    setError(null)
    try {
      await signIn('resend-otp', { email: email.trim().toLowerCase() })
      setStep('code')
    } catch {
      setError('Failed to send code. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCode = async (e: React.FormEvent) => {
    e.preventDefault()
    if (code.length !== 6) return
    setIsLoading(true)
    setError(null)
    try {
      await signIn('resend-otp', { email: email.trim().toLowerCase(), code: code.trim() })
      onClose()
    } catch {
      setError('Incorrect or expired code.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.2 }}
        className="bg-neutral-900 border border-neutral-800 rounded-2xl p-8 w-full max-w-sm relative"
      >
        <button onClick={onClose} className="absolute top-4 right-4 w-7 h-7 rounded-lg flex items-center justify-center text-neutral-600 hover:text-neutral-300 hover:bg-neutral-800 transition-colors">
          <X size={14} />
        </button>

        <div className="flex justify-center mb-6">
          <img src="/logo-sq-white.png" alt="Footage" className="h-10 w-10 rounded-xl" />
        </div>

        <h2 className="text-[18px] font-semibold text-center text-white mb-1">Save your video</h2>
        <p className="text-sm text-neutral-500 text-center mb-6">Sign in to keep your selections and continue</p>

        {error && <p className="text-xs text-red-400 mb-4 text-center">{error}</p>}

        {step === 'email' ? (
          <>
            <button
              onClick={onSignInWithGoogle}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl border border-neutral-700 bg-neutral-800 hover:bg-neutral-700 text-white text-[13px] font-medium transition-colors mb-4"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Continue with Google
            </button>

            <div className="relative mb-4">
              <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-neutral-800" /></div>
              <div className="relative flex justify-center"><span className="bg-neutral-900 px-2 text-[11px] text-neutral-600">or</span></div>
            </div>

            <form onSubmit={handleEmail} className="space-y-3">
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                className="w-full bg-neutral-800 border border-neutral-700 rounded-xl px-4 py-3 text-[14px] text-white placeholder:text-neutral-500 focus:outline-none focus:border-blue-500/50 transition-colors"
              />
              <button type="submit" disabled={isLoading || !email.trim()} className="w-full px-4 py-3 rounded-xl bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-500 transition-colors disabled:opacity-40 flex items-center justify-center gap-2">
                {isLoading ? <Loader2 size={14} className="animate-spin" /> : null}
                Continue with email
              </button>
            </form>
          </>
        ) : (
          <div className="space-y-3">
            <button onClick={() => { setStep('email'); setCode(''); setError(null) }} className="flex items-center gap-1.5 text-xs text-neutral-500 hover:text-neutral-300 transition-colors mb-2">
              <ChevronLeft size={13} /> Back
            </button>
            <p className="text-xs text-neutral-500 text-center">Enter the 6-digit code sent to <span className="text-white">{email}</span></p>
            <form onSubmit={handleCode} className="space-y-3">
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                autoFocus
                className="w-full bg-neutral-800 border border-neutral-700 rounded-xl px-4 py-3 text-[14px] text-white text-center tracking-widest placeholder:text-neutral-500 focus:outline-none focus:border-blue-500/50 transition-colors"
              />
              <button type="submit" disabled={isLoading || code.length !== 6} className="w-full px-4 py-3 rounded-xl bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-500 transition-colors disabled:opacity-40 flex items-center justify-center gap-2">
                {isLoading ? <Loader2 size={14} className="animate-spin" /> : null}
                Verify
              </button>
            </form>
          </div>
        )}
      </motion.div>
    </motion.div>
  )
}

// --- NuxAgentPanel ---

function NuxAgentPanel({
  message, suggestedActions, showTextInput, inputValue, onInputChange, onSubmit, onChoiceClick, onShowTextInput, onSendSelection, injectThumbnails,
}: {
  message: ChatMessage; suggestedActions: string[]; showTextInput: boolean; inputValue: string
  onInputChange: (v: string) => void; onSubmit: (e?: React.FormEvent) => void
  onChoiceClick: (action: string) => void; onShowTextInput: () => void
  onSendSelection: (message: string) => void
  injectThumbnails: (data: Record<string, unknown>) => Record<string, unknown>
}) {
  const visualToolCalls = message.toolCalls?.filter(tc => {
    if (tc.status !== 'completed' || !tc.result?.success) return false
    const data = tc.result?.data as { type?: string } | undefined
    return data?.type && ['media_grid', 'media_clusters', 'folder_preview'].includes(data.type)
  }) ?? []

  const cleanContent = (text: string | undefined, maxSentences = 2) => {
    if (!text) return ''
    let cleaned = text
      .replace(/<suggestedActions>[\s\S]*?<\/suggestedActions>/gi, '')
      .replace(/\n[^\n]*(?:\s[•·]\s[^\n]*){2,}/gm, '')
      .replace(/\n\*\*[^*]+\*\*\s*[—–-]\s*[^\n]+/gm, '')
      .replace(/\n[^\n]*(?:,\s[^\n,]+){1,}(?:,?\sor\s[^\n.?]+)[.?]?\s*$/m, '')
      .trim()
    const sentences = cleaned.match(/[^.!?]+[.!?]+/g)
    if (sentences && sentences.length > maxSentences) cleaned = sentences.slice(0, maxSentences).join('').trim()
    if (cleaned.length > 180) {
      const dotPos = cleaned.lastIndexOf('.', 180)
      if (dotPos > 80) { cleaned = cleaned.slice(0, dotPos + 1).trim() }
      else {
        const cutoff = Math.max(cleaned.lastIndexOf(' — ', 180), cleaned.lastIndexOf(', ', 180))
        cleaned = cutoff > 60 ? cleaned.slice(0, cutoff).trim() + '.' : cleaned.slice(0, 180).replace(/\s+\S*$/, '').trim() + '.'
      }
    }
    return cleaned
  }

  const content = cleanContent(message.content)
  const contentAfterTools = cleanContent(message.contentAfterTools)

  return (
    <div className="flex-1 flex flex-col justify-center">
      <div className="px-8 py-6 flex flex-col gap-5">
        {content && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }} className="text-[18px] leading-[1.55] text-white/90 font-light">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ p: ({ children }) => <p className="mb-0">{children}</p> }}>
              {content}
            </ReactMarkdown>
          </motion.div>
        )}

        {visualToolCalls.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }} className="space-y-4">
            {visualToolCalls.map(tc => {
              const rawData = tc.result!.data as Record<string, unknown>
              const data = rawData.type === 'media_clusters' ? injectThumbnails(rawData) : rawData
              return <div key={tc.id} className="w-full"><VisualToolWidget data={data} layout="vertical" onSendSelection={onSendSelection} /></div>
            })}
          </motion.div>
        )}

        {contentAfterTools && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.2 }} className="prose prose-invert max-w-none text-neutral-300 leading-relaxed text-[15px]">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{contentAfterTools}</ReactMarkdown>
          </motion.div>
        )}

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.4 }} className="mt-1">
          {showTextInput ? (
            <form onSubmit={onSubmit} className="flex gap-3">
              <input type="text" value={inputValue} onChange={e => onInputChange(e.target.value)} placeholder="Tell me what you'd like to create..." className="flex-1 bg-neutral-900 border border-neutral-700 rounded-xl px-4 py-3 text-[14px] text-white placeholder:text-neutral-500 focus:outline-none focus:border-blue-500/50 transition-colors" autoFocus />
              <button type="submit" disabled={!inputValue.trim()} className="px-4 py-3 rounded-xl bg-blue-600 text-white text-sm font-medium hover:bg-blue-500 transition-colors disabled:opacity-30 flex items-center gap-2">
                <Send size={14} />
              </button>
            </form>
          ) : (
            <NuxChoiceButtons actions={suggestedActions} onChoiceClick={onChoiceClick} onOtherClick={onShowTextInput} />
          )}
        </motion.div>
      </div>
    </div>
  )
}

// --- NuxMediaTimeline: vertical timeline with cluster piles ---

function PhotosPermissionBanner() {
  const { state, rescan } = useTimeline()
  const [opened, setOpened] = useState(false)

  if (state.blockedLibraries.length === 0) return null

  return (
    <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="mb-4 rounded-xl border border-amber-500/20 bg-amber-500/5 p-3">
      <div className="flex items-center gap-3">
        <Image size={14} className="text-amber-400 shrink-0" />
        <p className="text-xs text-neutral-400 flex-1">Photos Library needs Full Disk Access</p>
        {!opened ? (
          <button onClick={() => { openFullDiskAccess(); setOpened(true) }} className="text-[11px] font-medium px-2.5 py-1 rounded-lg bg-amber-500/20 text-amber-300 hover:bg-amber-500/30 transition-colors shrink-0">
            Grant Access
          </button>
        ) : (
          <button onClick={rescan} className="text-[11px] font-medium px-2.5 py-1 rounded-lg bg-blue-500/20 text-blue-300 hover:bg-blue-500/30 transition-colors shrink-0">
            Re-scan
          </button>
        )}
      </div>
    </motion.div>
  )
}

function NuxMediaTimeline() {
  const { state, dispatch } = useTimeline()
  const { clusters, selectedIds, scanProgress, scanThumbnails, scanDone } = state
  const timelineRef = useRef<HTMLDivElement>(null)

  const clustersWithThumbnails = useMemo(() => {
    return clusters.map(c => ({
      ...c,
      cover_thumbnail: scanThumbnails.get(c.id) || c.cover_thumbnail,
    }))
  }, [clusters, scanThumbnails])

  const timelineGroups = useMemo(
    () => groupByZoom(clustersWithThumbnails, state.zoomLevel),
    [clustersWithThumbnails, state.zoomLevel],
  )

  // Flat merged IDs for keyboard nav
  const flatMergedIds = useMemo(() => {
    return timelineGroups.flatMap(g =>
      g.merged.slice(0, state.groupLimits?.get(g.key) ?? 10).map(m => m.id)
    )
  }, [timelineGroups, state.groupLimits])

  // Keyboard navigation
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (state.lightbox || state.expandedClusterId) return
      const target = e.target as HTMLElement
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return

      if (e.key === 'ArrowDown' || e.key === 'j') {
        e.preventDefault()
        const next = Math.min(state.focusedClusterIndex + 1, flatMergedIds.length - 1)
        dispatch({ type: 'FOCUS_CLUSTER', index: next })
        document.getElementById(`cluster-${flatMergedIds[next]}`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      } else if (e.key === 'ArrowUp' || e.key === 'k') {
        e.preventDefault()
        const next = Math.max(state.focusedClusterIndex - 1, 0)
        dispatch({ type: 'FOCUS_CLUSTER', index: next })
        document.getElementById(`cluster-${flatMergedIds[next]}`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      } else if (e.key === ' ' && state.focusedClusterIndex >= 0) {
        e.preventDefault()
        const merged = timelineGroups.flatMap(g => g.merged).find(m => m.id === flatMergedIds[state.focusedClusterIndex])
        if (merged) merged.sourceIds.forEach(id => dispatch({ type: 'TOGGLE_CLUSTER', id }))
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [state.focusedClusterIndex, state.lightbox, state.expandedClusterId, flatMergedIds, timelineGroups, dispatch])

  const isScanning = !scanDone
  const fileCount = scanProgress?.filesFound ?? 0

  if (isScanning) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
          <span className="text-sm text-neutral-400">
            {fileCount > 0 ? `${fileCount.toLocaleString()} files found` : 'Scanning...'}
          </span>
        </div>
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <motion.div key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.1 }} className="flex gap-3">
              <div className="w-16 shrink-0" />
              <div className="flex gap-1.5 flex-1">
                {Array.from({ length: 3 }).map((_, j) => (
                  <div key={j} className="w-[100px] aspect-[4/3] rounded-lg bg-neutral-800/40 animate-pulse" />
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    )
  }

  if (clusters.length === 0) {
    return (
      <div className="flex-1 flex flex-col p-6">
        <PhotosPermissionBanner />
        <div className="flex-1 flex items-center justify-center text-neutral-600 text-sm">
          No media found
        </div>
      </div>
    )
  }

  const selectedCount = selectedIds.size
  const totalCount = clusters.length
  const totalFiles = clusters.reduce((s, c) => s + c.file_count, 0)
  const totalSize = clusters.reduce((s, c) => s + c.files.reduce((fs, f) => fs + f.size, 0), 0)
  const selectedFiles = clusters.filter(c => selectedIds.has(c.id)).reduce((s, c) => s + c.file_count, 0)
  const zoomLabels = ['Months', 'Days', 'Clusters'] as const
  const viewModes: { mode: ViewMode; icon: typeof List }[] = [
    { mode: 'list', icon: List },
    { mode: 'grid', icon: LayoutGrid },
    { mode: 'clusters', icon: Grid3x3 },
  ]

  return (
    <div className="relative h-full flex flex-col">
      <div className="flex-1 overflow-y-auto p-6" ref={timelineRef}>
        <PhotosPermissionBanner />

        {/* Header bar */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2 text-xs text-neutral-500">
            <span>{totalFiles.toLocaleString()} files</span>
            <span className="text-neutral-700">/</span>
            <span>{totalCount} groups</span>
            <span className="text-neutral-700">/</span>
            <span className="flex items-center gap-1"><HardDrive size={10} />{formatBytes(totalSize)}</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <button onClick={() => dispatch({ type: 'SELECT_ALL' })} className={cn('text-[11px] px-2 py-0.5 rounded transition-colors', selectedCount === totalCount ? 'text-neutral-600' : 'text-neutral-400 hover:text-neutral-200')}>All</button>
              <button onClick={() => dispatch({ type: 'DESELECT_ALL' })} className={cn('text-[11px] px-2 py-0.5 rounded transition-colors', selectedCount === 0 ? 'text-neutral-600' : 'text-neutral-400 hover:text-neutral-200')}>None</button>
            </div>
            <div className="w-px h-4 bg-neutral-800" />
            {/* View mode toggle */}
            <div className="flex items-center bg-neutral-900 rounded-md border border-neutral-800 p-0.5">
              {viewModes.map(({ mode, icon: Icon }) => (
                <button
                  key={mode}
                  onClick={() => dispatch({ type: 'SET_VIEW_MODE', mode })}
                  className={cn(
                    'h-6 w-6 rounded flex items-center justify-center transition-colors',
                    state.viewMode === mode ? 'bg-neutral-700 text-white' : 'text-neutral-500 hover:text-neutral-300',
                  )}
                >
                  <Icon size={12} />
                </button>
              ))}
            </div>
            {/* Zoom level toggle */}
            <div className="flex items-center bg-neutral-900 rounded-md border border-neutral-800 p-0.5">
              {([0, 1, 2] as ZoomLevel[]).map(level => (
                <button
                  key={level}
                  onClick={() => dispatch({ type: 'SET_ZOOM', level })}
                  className={cn(
                    'h-6 rounded flex items-center justify-center transition-colors px-2',
                    state.zoomLevel === level ? 'bg-neutral-700 text-white' : 'text-neutral-500 hover:text-neutral-300',
                  )}
                >
                  <span className="text-[10px] font-medium">{zoomLabels[level]}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Grid view: flat grid of all clusters */}
        {state.viewMode === 'grid' ? (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-2">
            {timelineGroups.flatMap(g => g.merged).map((merged, flatIdx) => (
              <MergedClusterCard
                key={merged.id}
                merged={merged}
                index={flatIdx}
                viewMode="grid"
                isFocused={state.focusedClusterIndex === flatIdx}
              />
            ))}
          </div>
        ) : (
        /* Timeline */
        <div className="relative pl-[100px]">
          {/* Vertical line with gradient fade */}
          <div className="absolute left-[93px] top-0 bottom-0 w-px">
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-neutral-800/50 to-transparent" />
          </div>

          <div className="space-y-6">
            {timelineGroups.map((group) => {
              const groupFiles = group.merged.reduce((s, m) => s + m.file_count, 0)
              const groupVideos = group.merged.reduce((s, m) => s + m.files.filter(f => f.type === 'video').length, 0)
              const groupImages = group.merged.reduce((s, m) => s + m.files.filter(f => f.type === 'image').length, 0)
              const groupSize = group.merged.reduce((s, m) => s + m.files.reduce((fs, f) => fs + f.size, 0), 0)
              const dotSize = groupFiles > 100 ? 'w-3.5 h-3.5' : groupFiles > 30 ? 'w-3 h-3' : 'w-2.5 h-2.5'
              const limit = state.groupLimits?.get(group.key) ?? 10

              return (
                <div key={group.key} className="relative">
                  {/* Date label + dot */}
                  <div className="absolute -left-[100px] top-0 w-[93px] flex flex-col items-end pr-3">
                    <span className="text-[11px] font-semibold text-neutral-400 whitespace-nowrap">{group.label}</span>
                    {group.dateLabel && <span className="text-[10px] text-neutral-600 whitespace-nowrap">{group.dateLabel}</span>}
                  </div>
                  <div className={cn('absolute top-[3px] -left-[7px] rounded-full bg-neutral-700 border-[2.5px] border-neutral-950 z-10', dotSize)} />

                  {/* Merged clusters */}
                  <div className={cn(
                    'pl-3',
                    state.viewMode === 'clusters' ? 'space-y-3' : 'space-y-2',
                  )}>
                    {/* Group stats */}
                    <div className="flex items-center gap-2 text-[10px] text-neutral-600 mb-1 tabular-nums">
                      {groupImages > 0 && <span className="flex items-center gap-0.5"><Image size={9} />{groupImages}</span>}
                      {groupVideos > 0 && <span className="flex items-center gap-0.5"><Play size={9} />{groupVideos}</span>}
                      <span className="text-neutral-700">·</span>
                      <span>{formatBytes(groupSize)}</span>
                      {group.merged.length > 1 && state.viewMode !== 'clusters' && (
                        <><span className="text-neutral-700">·</span><span>{group.merged[0].sourceIds.length} clusters merged</span></>
                      )}
                    </div>
                    {group.merged.slice(0, limit).map((merged) => {
                      const flatIdx = flatMergedIds.indexOf(merged.id)
                      return (
                        <MergedClusterCard
                          key={merged.id}
                          merged={merged}
                          index={flatIdx}
                          viewMode={state.viewMode}
                          isFocused={state.focusedClusterIndex === flatIdx}
                        />
                      )
                    })}
                    {group.merged.length > limit && (
                      <button
                        onClick={() => dispatch({ type: 'SHOW_MORE', group: group.key, count: limit + 20 })}
                        className="text-[11px] text-neutral-500 hover:text-neutral-300 py-1 transition-colors"
                      >
                        Show {Math.min(20, group.merged.length - limit)} more...
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
        )}
      </div>

      {/* Sticky selection bar */}
      <AnimatePresence>
        {selectedCount > 0 && (
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="sticky bottom-0 px-4 py-3 border-t border-neutral-800/60 bg-neutral-950/90 backdrop-blur-md flex items-center justify-between"
          >
            <div className="flex items-center gap-3 text-[12px]">
              <div className="flex items-center gap-1.5 text-white font-medium">
                <div className="w-5 h-5 rounded bg-blue-500 flex items-center justify-center text-[10px] font-bold">{selectedCount}</div>
                <span>{selectedCount === 1 ? 'group' : 'groups'} selected</span>
              </div>
              <span className="text-neutral-600">·</span>
              <span className="text-neutral-400">{selectedFiles.toLocaleString()} files</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => dispatch({ type: 'DESELECT_ALL' })}
                className="text-[11px] px-2.5 py-1 rounded text-neutral-400 hover:text-neutral-200 transition-colors"
              >
                Clear
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// --- MergedClusterCard ---

function MergedClusterCard({
  merged,
  index,
  viewMode,
  isFocused,
}: {
  merged: MergedCluster
  index: number
  viewMode: ViewMode
  isFocused: boolean
}) {
  const { state, dispatch } = useTimeline()
  const isSelected = merged.sourceIds.every(id => state.selectedIds.has(id))
  const isPartial = !isSelected && merged.sourceIds.some(id => state.selectedIds.has(id))
  const isExpanded = state.expandedClusterId === merged.id
  const loadedRef = useRef(false)
  const totalSize = useMemo(() => formatBytes(merged.files.reduce((s, f) => s + f.size, 0)), [merged.files])

  const shouldLoadFiles = isExpanded
  useEffect(() => {
    if (!shouldLoadFiles || loadedRef.current) return
    loadedRef.current = true
    const paths = merged.files
      .filter(f => f.type === 'image' || f.type === 'video')
      .slice(0, 60)
      .map(f => f.path)
      .filter(p => !state.fileThumbnails.has(p))
    if (paths.length > 0) {
      batchThumbnails(paths).then(results => {
        dispatch({ type: 'SET_FILE_THUMBNAILS', thumbnails: results })
      })
    }
  }, [shouldLoadFiles, merged.files, state.fileThumbnails, dispatch])

  const toggleAll = (e: React.MouseEvent) => {
    e.stopPropagation()
    merged.sourceIds.forEach(id => {
      if (isSelected) {
        if (state.selectedIds.has(id)) dispatch({ type: 'TOGGLE_CLUSTER', id })
      } else {
        if (!state.selectedIds.has(id)) dispatch({ type: 'TOGGLE_CLUSTER', id })
      }
    })
  }

  const checkbox = (
    <button
      onClick={toggleAll}
      className={cn(
        'rounded border flex items-center justify-center shrink-0 transition-all w-5 h-5',
        isSelected ? 'bg-blue-500 border-blue-500' : isPartial ? 'bg-blue-500/40 border-blue-500/60' : 'border-neutral-700 hover:border-neutral-500',
      )}
    >
      {(isSelected || isPartial) && <Check size={11} className="text-white" />}
    </button>
  )

  const emptyThumb = (
    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-neutral-800 to-neutral-900">
      <Film className="h-5 w-5 text-neutral-700" />
    </div>
  )

  const focusRing = isFocused ? 'ring-1 ring-blue-500/50' : ''
  const isMergedGroup = merged.sourceIds.length > 1

  // Thumbnail mosaic for merged groups (shows multiple source thumbs)
  const thumbMosaic = (w: string, h: string) => {
    const thumbs = merged.thumbnails.slice(0, 4)
    if (thumbs.length <= 1) {
      return (
        <div className={cn('relative shrink-0 rounded-lg overflow-hidden border border-neutral-700/40 shadow-lg', w, h)}>
          {merged.cover_thumbnail ? (
            <img src={merged.cover_thumbnail} alt="" className="w-full h-full object-cover" />
          ) : emptyThumb}
        </div>
      )
    }
    return (
      <div className={cn('relative shrink-0 rounded-lg overflow-hidden border border-neutral-700/40 shadow-lg grid gap-px', w, h, thumbs.length <= 2 ? 'grid-cols-2' : 'grid-cols-2 grid-rows-2')}>
        {thumbs.map((t, i) => (
          <img key={i} src={t} alt="" className="w-full h-full object-cover" />
        ))}
      </div>
    )
  }

  // --- List view: compact row with mosaic ---
  if (viewMode === 'list') {
    return (
      <motion.div
        id={`cluster-${merged.id}`}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: Math.min(index * 0.02, 0.3) }}
        className={cn('rounded-lg transition-all duration-200', focusRing, isSelected || isPartial ? 'opacity-100' : 'opacity-40')}
      >
        <div
          className="flex items-center gap-3 group cursor-pointer py-1.5"
          onClick={() => dispatch({ type: 'EXPAND_CLUSTER', id: merged.id })}
        >
          {thumbMosaic('w-[88px]', 'h-[60px]')}
          <div className="flex-1 min-w-0">
            <div className="text-[12px] font-medium text-white/90 truncate">{merged.label}</div>
            <div className="flex items-center gap-1.5 mt-0.5 text-[10px] text-neutral-500 tabular-nums">
              <span>{merged.file_count} files</span>
              <span className="text-neutral-700">·</span>
              <span>{totalSize}</span>
              {isMergedGroup && <><span className="text-neutral-700">·</span><span>{merged.sourceIds.length} clusters</span></>}
            </div>
          </div>
          {checkbox}
        </div>

        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden"
            >
              <div className="pt-2 pb-3">
                <div className="grid grid-cols-5 xl:grid-cols-6 gap-1">
                  {merged.files.slice(0, 60).map((file, fi) => (
                    <FileThumb
                      key={file.path}
                      file={file}
                      thumbnail={state.fileThumbnails.get(file.path)}
                      onClick={() => dispatch({ type: 'OPEN_LIGHTBOX', clusterId: merged.sourceIds[0], fileIndex: fi })}
                      index={fi}
                    />
                  ))}
                  {merged.files.length > 60 && (
                    <div className="aspect-square rounded-md bg-neutral-800/30 flex items-center justify-center">
                      <span className="text-[11px] text-neutral-600">+{merged.files.length - 60}</span>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    )
  }

  // --- Grid view: thumbnail grid cards ---
  if (viewMode === 'grid') {
    return (
      <motion.div
        id={`cluster-${merged.id}`}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: Math.min(index * 0.02, 0.3) }}
        className={cn('relative group cursor-pointer rounded-lg overflow-hidden transition-all duration-200', focusRing, isSelected || isPartial ? 'opacity-100' : 'opacity-40')}
        onClick={() => dispatch({ type: 'EXPAND_CLUSTER', id: merged.id })}
      >
        <div className="aspect-[4/3] relative">
          {merged.cover_thumbnail ? (
            <img src={merged.cover_thumbnail} alt="" className="w-full h-full object-cover" />
          ) : emptyThumb}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
          <div className="absolute bottom-2 left-2 right-2 flex items-end justify-between">
            <div className="min-w-0">
              <div className="text-[11px] font-medium text-white truncate">{merged.folder_name || merged.label}</div>
              <div className="flex items-center gap-1 text-[9px] text-white/60 tabular-nums">
                {merged.media_types.includes('video') && <Play size={8} />}
                {merged.media_types.includes('image') && <Image size={8} />}
                <span>{merged.file_count}</span>
                <span>·</span>
                <span>{totalSize}</span>
              </div>
            </div>
            {checkbox}
          </div>
        </div>

        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden bg-neutral-900"
            >
              <div className="p-2">
                <div className="grid grid-cols-4 gap-1">
                  {merged.files.slice(0, 40).map((file, fi) => (
                    <FileThumb
                      key={file.path}
                      file={file}
                      thumbnail={state.fileThumbnails.get(file.path)}
                      onClick={() => dispatch({ type: 'OPEN_LIGHTBOX', clusterId: merged.sourceIds[0], fileIndex: fi })}
                      index={fi}
                    />
                  ))}
                  {merged.files.length > 40 && (
                    <div className="aspect-square rounded-md bg-neutral-800/30 flex items-center justify-center">
                      <span className="text-[11px] text-neutral-600">+{merged.files.length - 40}</span>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    )
  }

  // --- Clusters view: detailed card ---
  return (
    <motion.div
      id={`cluster-${merged.id}`}
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: Math.min(index * 0.02, 0.3) }}
      className={cn('rounded-xl transition-all duration-200', focusRing, isSelected || isPartial ? 'opacity-100' : 'opacity-40')}
    >
      <div
        className="flex items-start gap-4 group cursor-pointer py-2"
        onClick={() => dispatch({ type: 'EXPAND_CLUSTER', id: merged.id })}
      >
        <div className="relative w-[140px] h-[96px] shrink-0">
          {merged.file_count > 2 && (
            <div className="absolute top-0 left-[8px] w-[124px] h-[84px] rounded-lg bg-neutral-800 border border-neutral-700/30 rotate-[3deg]" />
          )}
          {merged.file_count > 1 && (
            <div className="absolute top-[2px] left-[4px] w-[132px] h-[88px] rounded-lg bg-neutral-800 border border-neutral-700/30 rotate-[-2deg]" />
          )}
          <div className="absolute inset-0 rounded-lg overflow-hidden border border-neutral-700/40 shadow-lg">
            {merged.cover_thumbnail ? (
              <img src={merged.cover_thumbnail} alt="" className="w-full h-full object-cover" />
            ) : emptyThumb}
            {merged.media_types.length === 1 && merged.media_types[0] === 'video' && (
              <div className="absolute bottom-1.5 right-1.5 w-5 h-5 rounded bg-black/60 flex items-center justify-center">
                <Play size={10} className="text-white ml-px" />
              </div>
            )}
          </div>
          {merged.file_count > 1 && (
            <div className="absolute -bottom-1 -right-1 bg-neutral-900 border border-neutral-700 rounded px-1.5 py-0.5 text-[10px] font-medium text-neutral-400 tabular-nums z-10">
              {merged.file_count}
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0 pt-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[14px] font-medium text-white/90 truncate">{merged.label}</div>
              <div className="flex items-center gap-1.5 mt-1 text-[11px] text-neutral-500">
                {merged.media_types.includes('video') && <Play size={10} />}
                {merged.media_types.includes('image') && <Image size={10} />}
                <span>{merged.file_count} {merged.file_count === 1 ? 'file' : 'files'}</span>
                <span className="text-neutral-700">·</span>
                <span>{totalSize}</span>
              </div>
              {merged.folder_name && !merged.label.includes(merged.folder_name) && (
                <div className="flex items-center gap-1.5 mt-1 text-[10px] text-neutral-600">
                  <span className="truncate max-w-[140px]">{merged.folder_name}</span>
                </div>
              )}
            </div>
            {checkbox}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="pt-2 pb-3">
              <div className="grid grid-cols-4 xl:grid-cols-5 gap-1.5">
                {merged.files.slice(0, 40).map((file, fi) => (
                  <FileThumb
                    key={file.path}
                    file={file}
                    thumbnail={state.fileThumbnails.get(file.path)}
                    onClick={() => dispatch({ type: 'OPEN_LIGHTBOX', clusterId: merged.sourceIds[0], fileIndex: fi })}
                    index={fi}
                  />
                ))}
                {merged.files.length > 40 && (
                  <div className="aspect-square rounded-md bg-neutral-800/30 flex items-center justify-center">
                    <span className="text-[11px] text-neutral-600">+{merged.files.length - 40}</span>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

// --- FileThumb ---

function FileThumb({
  file,
  thumbnail,
  onClick,
  index,
}: {
  file: { path: string; name: string; type: string; size: number }
  thumbnail?: string
  onClick: () => void
  index: number
}) {
  const isVideo = file.type === 'video'
  return (
    <motion.button
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.15, delay: Math.min(index * 0.01, 0.3) }}
      onClick={onClick}
      className="aspect-square rounded-md overflow-hidden relative group bg-gradient-to-br from-neutral-800/50 to-neutral-900/50 hover:ring-1 hover:ring-white/20 transition-all"
    >
      {thumbnail ? (
        <img src={thumbnail} alt="" className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center gap-1">
          {isVideo ? <Play size={12} className="text-neutral-600" /> : <Image size={12} className="text-neutral-600" />}
          <span className="text-[8px] text-neutral-700 truncate max-w-[90%] px-1">{file.name}</span>
        </div>
      )}
      {isVideo && thumbnail && (
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="w-6 h-6 rounded-full bg-black/60 flex items-center justify-center">
            <Play size={10} className="text-white ml-px" />
          </div>
        </div>
      )}
    </motion.button>
  )
}

// --- MediaLightbox ---

function MediaLightbox() {
  const { state, dispatch } = useTimeline()
  const { lightbox, clusters, fileThumbnails } = state

  const cluster = lightbox ? clusters.find(c => c.id === lightbox.clusterId) : null
  const file = cluster?.files[lightbox?.fileIndex ?? -1]
  const maxIndex = (cluster?.files.length ?? 1) - 1

  useEffect(() => {
    if (!lightbox) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dispatch({ type: 'CLOSE_LIGHTBOX' })
      else if (e.key === 'ArrowLeft') dispatch({ type: 'LIGHTBOX_PREV' })
      else if (e.key === 'ArrowRight') dispatch({ type: 'LIGHTBOX_NEXT', maxIndex })
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [lightbox, maxIndex, dispatch])

  useEffect(() => {
    if (!file || fileThumbnails.has(file.path)) return
    batchThumbnails([file.path]).then(results => {
      dispatch({ type: 'SET_FILE_THUMBNAILS', thumbnails: results })
    })
  }, [file, fileThumbnails, dispatch])

  if (!lightbox || !file || !cluster) return null

  const isVideo = file.type === 'video'
  const isImage = file.type === 'image'
  const mediaUrl = localMediaUrl(file.path)
  const thumb = fileThumbnails.get(file.path)
  const canPrev = lightbox.fileIndex > 0
  const canNext = lightbox.fileIndex < maxIndex

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/95 flex flex-col"
      onClick={(e) => { if (e.target === e.currentTarget) dispatch({ type: 'CLOSE_LIGHTBOX' }) }}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-5 py-3 shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-[13px] font-medium text-white truncate max-w-[280px]">{file.name}</span>
          <span className="text-[11px] text-neutral-600">{lightbox.fileIndex + 1} / {cluster.files.length}</span>
        </div>
        <button onClick={() => dispatch({ type: 'CLOSE_LIGHTBOX' })} className="w-8 h-8 rounded-lg flex items-center justify-center text-neutral-500 hover:text-white hover:bg-neutral-800 transition-colors">
          <X size={16} />
        </button>
      </div>

      {/* Media */}
      <div className="flex-1 flex items-center justify-center min-h-0 px-14 pb-4 relative">
        {canPrev && (
          <button onClick={() => dispatch({ type: 'LIGHTBOX_PREV' })} className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-neutral-900/80 border border-neutral-800 flex items-center justify-center text-neutral-400 hover:text-white transition-colors z-10">
            <ChevronLeft size={18} />
          </button>
        )}

        <div className="w-full h-full flex items-center justify-center">
          {isVideo ? (
            <div key={file.path} className="w-full h-full">
              <PlyrPlayer
                src={mediaUrl}
                poster={thumb || undefined}
                autoPlay
              />
            </div>
          ) : isImage ? (
            <img
              key={file.path}
              src={mediaUrl}
              alt={file.name}
              className="max-w-full max-h-full rounded-lg object-contain"
              onError={(e) => { if (thumb) (e.target as HTMLImageElement).src = thumb }}
            />
          ) : thumb ? (
            <img src={thumb} alt={file.name} className="max-w-full max-h-full rounded-lg object-contain" />
          ) : (
            <div className="w-48 h-48 rounded-xl bg-neutral-900 flex flex-col items-center justify-center gap-2">
              <Music size={28} className="text-neutral-600" />
              <span className="text-xs text-neutral-500">{file.name}</span>
            </div>
          )}
        </div>

        {canNext && (
          <button onClick={() => dispatch({ type: 'LIGHTBOX_NEXT', maxIndex })} className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-neutral-900/80 border border-neutral-800 flex items-center justify-center text-neutral-400 hover:text-white transition-colors z-10">
            <ChevronRight size={18} />
          </button>
        )}
      </div>
    </motion.div>
  )
}

// --- NuxChoiceButtons ---

function NuxChoiceButtons({ actions, onChoiceClick, onOtherClick }: { actions: string[]; onChoiceClick: (action: string) => void; onOtherClick: () => void }) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return
      const num = parseInt(e.key, 10)
      if (num >= 1 && num <= actions.length) { e.preventDefault(); onChoiceClick(actions[num - 1]) }
      else if (e.key === 'Enter' && actions.length > 0) { e.preventDefault(); onChoiceClick(actions[0]) }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [actions, onChoiceClick])

  if (actions.length === 0) return null

  return (
    <div className="flex flex-col gap-2">
      {actions.map((action, i) => (
        <motion.button key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: i * 0.06 }} onClick={() => onChoiceClick(action)} whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.995 }}
          className={cn('w-full text-left px-4 py-2.5 rounded-xl border transition-all duration-200 flex items-center justify-between gap-3',
            i === 0 ? 'border-blue-600 bg-blue-600 text-white hover:bg-blue-500 hover:border-blue-500 hover:shadow-[0_0_24px_rgba(59,130,246,0.25)]' : 'border-neutral-800 bg-neutral-900/50 text-neutral-200 hover:border-neutral-600 hover:bg-neutral-800/60',
          )}>
          <div className="flex items-center gap-3">
            <span className="text-[11px] font-mono opacity-40">{i + 1}</span>
            <span className="text-[13px] font-medium">{action}</span>
          </div>
          <ArrowRight size={14} className={cn('shrink-0', i === 0 ? 'text-white/70' : 'text-neutral-600')} />
        </motion.button>
      ))}
      <motion.button initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3, delay: actions.length * 0.06 + 0.1 }} onClick={onOtherClick} className="w-full text-left px-4 py-2 text-neutral-500 hover:text-neutral-300 transition-colors">
        <span className="text-[12px]">Something else...</span>
      </motion.button>
    </div>
  )
}

// --- InitErrorView ---

function InitErrorView({ error, onRetry, onSkip }: { error: string; onRetry: () => void; onSkip: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center text-center px-6">
      <h1 className="text-xl font-semibold tracking-tight text-white">Something went wrong</h1>
      <p className="text-neutral-500 mt-2 text-sm">{error}</p>
      <div className="flex gap-3 mt-6">
        <button onClick={onRetry} className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm transition-colors">Retry</button>
        <button onClick={onSkip} className="px-4 py-2 rounded-lg bg-neutral-800 hover:bg-neutral-700 text-neutral-300 text-sm transition-colors">Skip</button>
      </div>
    </div>
  )
}

// --- DevPanel ---

function DevPanel({ user, workflowId }: { user: any; workflowId: Id<'workflows'> | null }) {
  const [open, setOpen] = useState(false)
  const [settings, setSettings] = useState<DesktopSettings | null>(null)
  const resetNux = useMutation(api.userProfiles.resetNux)
  const navigate = useNavigate()

  useEffect(() => {
    if (open && !settings) { getDesktopSettings().then(s => s && setSettings(s)) }
  }, [open, settings])

  const handleResetNux = async () => {
    if (user) { try { await resetNux() } catch {} }
    window.location.reload()
  }

  const handleToggleUrl = async () => {
    if (!settings) return
    const useLocal = !settings.useLocal
    await setDesktopSettings({ useLocal })
    setSettings({ ...settings, useLocal })
  }

  if (!isElectron()) return null

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {!open && (
        <button onClick={() => setOpen(true)} className="w-8 h-8 rounded-full bg-neutral-900 border border-neutral-800 flex items-center justify-center text-neutral-600 hover:text-neutral-400 hover:border-neutral-700 transition-colors" title="Dev controls">
          <Settings size={14} />
        </button>
      )}
      {open && (
        <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-3 space-y-3 w-64 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-neutral-400 font-medium text-xs uppercase tracking-wider">Dev</span>
            <button onClick={() => setOpen(false)} className="text-neutral-600 hover:text-neutral-400">&times;</button>
          </div>
          <button onClick={handleResetNux} className="w-full text-left px-2 py-1.5 rounded bg-neutral-800 hover:bg-neutral-700 text-neutral-300 transition-colors">
            Reset NUX{!user && ' (no auth)'}
          </button>
          <button onClick={() => navigate('/videos', { replace: true })} className="w-full text-left px-2 py-1.5 rounded bg-neutral-800 hover:bg-neutral-700 text-neutral-300 transition-colors">
            Skip to /videos
          </button>
          {workflowId && <div className="px-2 py-1 text-[10px] font-mono text-neutral-600 truncate">wf: {workflowId}</div>}
          {settings && (
            <div className="space-y-2 pt-1 border-t border-neutral-800">
              <div className="flex items-center justify-between">
                <span className="text-neutral-500 text-xs">{settings.useLocal ? 'local.footage' : SITE_HOST}</span>
                <button onClick={handleToggleUrl} className={cn('px-2 py-0.5 rounded text-xs font-medium transition-colors', settings.useLocal ? 'bg-amber-500/20 text-amber-400' : 'bg-emerald-500/20 text-emerald-400')}>
                  {settings.useLocal ? 'LOCAL' : 'PROD'}
                </button>
              </div>
              <button onClick={() => window.location.reload()} className="w-full text-left px-2 py-1.5 rounded bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 transition-colors text-xs">
                Apply & reload
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
